import type {
  CurrentUser,
  HealthStatus,
  OAuthAuthorization,
  PendingAuthorization,
  ReadinessStatus,
  SessionResult,
  WeComCallbackRequest,
} from './generated/types';
import { randomUuid } from './randomUuid';
import {
  recordRequestPerformance,
  startClientPerformancePhase,
} from './requestPerformance';
import {
  type ActiveRequestInit,
  createRoleViewDispatch,
  RoleViewSwitchInProgressError,
} from './roleViewDispatch';
import { isVerifiedSessionProjection } from './sessionProjection';

export class ActiveApiError extends Error {
  constructor(
    readonly status: number | null,
    message: string,
    readonly code?: string,
    readonly title?: string,
  ) {
    super(message);
  }
}

export type WeComEligibilityProblem = {
  kind: 'unknown' | 'denied';
  message: string;
};

export function weComEligibilityProblem(
  status: number | null,
  code: unknown,
  title: unknown,
): WeComEligibilityProblem | null {
  if (
    status === 503 &&
    code === 'DEPENDENCY_UNAVAILABLE' &&
    title === '暂时无法确认企业微信使用资格，请稍后重试'
  )
    return {
      kind: 'unknown',
      message: '暂时无法确认企业微信使用资格，请稍后重试。当前报价已保留。',
    };
  if (
    status === 401 &&
    (code === 'SESSION_REVOKED' || code === 'AUTHENTICATION_REQUIRED') &&
    title === '企业微信使用资格已失效，请联系管理员'
  )
    return {
      kind: 'denied',
      message: '企业微信使用资格已失效，请联系管理员。当前报价已保留。',
    };
  return null;
}

export type ActiveTransport = ReturnType<typeof createActiveTransport>;
// /me can revalidate a session rotated by another tab without inventing its TTL.
export type RecoveredSession = Pick<SessionResult, 'user' | 'csrfToken'> &
  Partial<Pick<SessionResult, 'accessExpiresAt'>>;

const SESSION_ROTATION_LOCK_TIMEOUT_MS = 5_000;
const SESSION_REFRESH_REQUEST_TIMEOUT_MS = 10_000;

export function createActiveTransport(
  baseUrl: string,
  options: { offlineDemo?: boolean } = {},
) {
  const roleViewDispatch = createRoleViewDispatch();
  const assertOnline = () => {
    if (options.offlineDemo)
      throw new ActiveApiError(
        null,
        '本地演示未连接服务器，该操作暂不可用。',
        'OFFLINE_DEMO',
      );
  };
  async function requestResponse(
    path: string,
    init: ActiveRequestInit = {},
  ): Promise<Response> {
    assertOnline();
    try {
      const headers = roleViewDispatch.headers(path, init);
      if (!headers.has('Accept')) headers.set('Accept', 'application/json');
      const { roleView: _roleView, ...fetchInit } = init;
      // Another tab or session recovery may rotate the HttpOnly cookie after
      // an API client captures its token. Read the paired token at dispatch.
      // Only same-origin, already authenticated mutations may use this cookie.
      const url = new URL(`${baseUrl}${path}`, window.location.href);
      if (
        url.origin === window.location.origin &&
        headers.has('X-CSRF-Token') &&
        !['GET', 'HEAD', 'OPTIONS'].includes(
          (init.method ?? 'GET').toUpperCase(),
        )
      ) {
        const cookie = document.cookie
          .split(';')
          .map((part) => part.trim())
          .find((part) => part.startsWith('cpq_csrf='));
        if (cookie)
          headers.set(
            'X-CSRF-Token',
            decodeURIComponent(cookie.slice('cpq_csrf='.length)),
          );
      }
      const startedAt =
        import.meta.env.VITE_CPQ_PERFORMANCE_ENABLED === 'true'
          ? performance.now()
          : 0;
      const response = await fetch(`${baseUrl}${path}`, {
        credentials: 'include',
        ...fetchInit,
        headers,
      });
      if (import.meta.env.VITE_CPQ_PERFORMANCE_ENABLED === 'true') {
        try {
          recordRequestPerformance(
            path,
            (init.method ?? 'GET').toUpperCase(),
            startedAt,
            response,
          );
        } catch {
          // 本机监测失败不能把已成功的业务写入转换成网络错误。
        }
      }
      return response;
    } catch (error) {
      if (error instanceof RoleViewSwitchInProgressError) throw error;
      if (init.signal?.aborted)
        throw new ActiveApiError(
          null,
          '连接报价系统超时或请求已取消，请重试。',
          init.signal.reason?.name === 'TimeoutError'
            ? 'REQUEST_TIMEOUT'
            : 'REQUEST_ABORTED',
        );
      throw new ActiveApiError(
        null,
        '无法连接报价系统后端。请确认后端地址及服务状态。',
        'NETWORK_ERROR',
      );
    }
  }
  async function request<T>(
    path: string,
    init: ActiveRequestInit = {},
  ): Promise<T> {
    const response = await requestResponse(path, init);
    if (!response.ok) {
      const problem = await response.json().catch(() => ({}));
      throw new ActiveApiError(
        response.status,
        weComEligibilityProblem(response.status, problem?.code, problem?.title)
          ?.message ??
          (response.status === 401
            ? '当前没有有效会话或会话已失效。'
            : `后端请求失败（HTTP ${response.status}）。`),
        typeof problem?.code === 'string' ? problem.code : undefined,
        typeof problem?.title === 'string' ? problem.title : undefined,
      );
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }
  return {
    getHealth: () => request<HealthStatus>('/healthz'),
    getReadiness: () => request<ReadinessStatus>('/readyz'),
    beginWeComAuthorization: () =>
      request<OAuthAuthorization>('/auth/wecom/authorize'),
    completeWeComAuthorization: (
      body: WeComCallbackRequest,
      idempotencyKey: string,
    ) =>
      request<SessionResult | PendingAuthorization>('/auth/wecom/callback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify(body),
      }),
    refreshSession: async (csrfToken: string): Promise<RecoveredSession> => {
      assertOnline();
      const rotate = async () => {
        // 同源 Tab 共用 HttpOnly cookie。取得跨 Tab 锁后再读与之配对的 CSRF，
        // 避免重复使用旧凭据触发服务端会话族撤销；不存储凭据或业务草稿。
        const cookie = document.cookie
          .split(';')
          .map((part) => part.trim())
          .find((part) => part.startsWith('cpq_csrf='));
        const currentCsrf =
          (cookie
            ? decodeURIComponent(cookie.slice('cpq_csrf='.length))
            : null) || csrfToken;
        if (currentCsrf !== csrfToken) {
          try {
            const user = await request<CurrentUser>('/me', {
              signal: AbortSignal.timeout(SESSION_REFRESH_REQUEST_TIMEOUT_MS),
            });
            const after = document.cookie
              .split(';')
              .map((part) => part.trim())
              .find((part) => part.startsWith('cpq_csrf='));
            if (
              !after ||
              decodeURIComponent(after.slice('cpq_csrf='.length)) !==
                currentCsrf
            )
              throw new ActiveApiError(
                null,
                '登录正在由其他页面更新，请重试连接。',
                'SESSION_CHANGED_DURING_REVALIDATION',
              );
            return { user, csrfToken: currentCsrf };
          } catch (error) {
            // Only ordinary access expiry is renewable; revocation and network
            // failures must not trigger another rotation with stale credentials.
            if (
              !(error instanceof ActiveApiError) ||
              error.status !== 401 ||
              !['AUTHENTICATION_REQUIRED', 'SESSION_EXPIRED'].includes(
                error.code ?? '',
              )
            )
              throw error;
          }
        }
        const selectedRoleId = roleViewDispatch.selectedRoleId;
        const rotated = await request<SessionResult>('/auth/refresh', {
          method: 'POST',
          signal: AbortSignal.timeout(SESSION_REFRESH_REQUEST_TIMEOUT_MS),
          headers: {
            'X-CSRF-Token': currentCsrf,
            'Idempotency-Key': randomUuid(),
          },
        });
        const pairedCookie = () => {
          const value = document.cookie
            .split(';')
            .map((part) => part.trim())
            .find((part) => part.startsWith('cpq_csrf='));
          return value
            ? decodeURIComponent(value.slice('cpq_csrf='.length))
            : null;
        };
        const rotatedCookie = pairedCookie();
        if (rotatedCookie !== null && rotatedCookie !== rotated.csrfToken)
          throw new ActiveApiError(
            null,
            '登录正在由其他页面更新，请重试连接。',
            'SESSION_CHANGED_DURING_REVALIDATION',
          );
        // Authentication rotation returns an unviewed principal. Capability and
        // target policy facts must always come from the actual /me projection.
        const user = await request<CurrentUser>('/me', {
          roleView: selectedRoleId ?? 'original',
          signal: AbortSignal.timeout(SESSION_REFRESH_REQUEST_TIMEOUT_MS),
        });
        if (
          pairedCookie() !== rotatedCookie ||
          roleViewDispatch.selectedRoleId !== selectedRoleId
        )
          throw new ActiveApiError(
            null,
            '登录正在由其他页面更新，请重试连接。',
            'SESSION_CHANGED_DURING_REVALIDATION',
          );
        if (!isVerifiedSessionProjection(rotated.user, user, selectedRoleId))
          throw new ActiveApiError(
            401,
            '会话恢复期间身份或验收角色已变化，未采用新的编辑权限。',
            'SESSION_IDENTITY_CHANGED',
          );
        return { ...rotated, user };
      };
      if (!navigator.locks) return rotate();
      const lockAbort = new AbortController();
      const lockTimer = window.setTimeout(
        () => lockAbort.abort(),
        SESSION_ROTATION_LOCK_TIMEOUT_MS,
      );
      const finishLockWait = startClientPerformancePhase(
        'session',
        'rotation-lock',
      );
      try {
        return await navigator.locks.request(
          'quotation-system:session-rotation',
          { signal: lockAbort.signal },
          async () => {
            // The five-second bound is only for acquiring the lock. Once held,
            // the HTTP request has its own independent timeout.
            window.clearTimeout(lockTimer);
            finishLockWait();
            return rotate();
          },
        );
      } catch (error) {
        if (lockAbort.signal.aborted)
          throw new ActiveApiError(
            null,
            '正在等待其他页面恢复登录，请稍后重试。',
            'SESSION_ROTATION_LOCK_TIMEOUT',
          );
        throw error;
      } finally {
        finishLockWait();
        window.clearTimeout(lockTimer);
      }
    },
    logoutSession: (csrfToken: string) =>
      request<void>('/auth/logout', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Idempotency-Key': randomUuid(),
        },
      }),
    getCurrentUser: (roleView?: 'original' | string) =>
      request<CurrentUser>('/me', { roleView }),
    getRoleViewOptions: () =>
      request<{
        items: Array<{
          id: string;
          key: string;
          name: string;
          active: boolean;
        }>;
      }>('/roles', { roleView: 'original' }),
    roleViewDispatch,
    request,
    requestResponse,
  };
}
